const primitiveColors = {
  zinc: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
    950: '#09090b',
  },
  brandBlue: {
    50: '#f2f8fd',
    100: '#e3effb',
    200: '#c1dff6',
    300: '#8ac5ef',
    400: '#4ba8e5',
    500: '#248ed3',
    600: '#1670b3',
    700: '#135991',
    800: '#144c78',
    900: '#133857',
    950: '#0f2942',
  },
  green: {
    50: '#f4faf3',
    100: '#e6f4e4',
    200: '#cee8ca',
    300: '#a8d4a1',
    400: '#82bd7a',
    500: '#559b4c',
    600: '#427f3a',
    700: '#366530',
    800: '#2e512a',
    900: '#274324',
    950: '#112310',
  },
  navy: {
    50: '#f8f8ed',
    100: '#edefd8',
    200: '#dce0b6',
    300: '#c6cb8b',
    400: '#adb566',
    500: '#909a48',
    600: '#717a36',
    700: '#565e2d',
    800: '#464c28',
    900: '#3d4225',
    950: '#1f2310',
  },
  orange: {
    50: '#fef8ee',
    100: '#fdefd7',
    200: '#fbdaad',
    300: '#f7bf7a',
    400: '#f39a44',
    500: '#f18832',
    600: '#e16315',
    700: '#bb4b13',
    800: '#943c18',
    900: '#783316',
    950: '#411709',
  },
  amber: {
    50: '#fefaec',
    100: '#faf1cb',
    200: '#f4e293',
    300: '#efcd5a',
    400: '#ebb934',
    500: '#de971b',
    600: '#c97816',
    700: '#a75716',
    800: '#884318',
    900: '#703817',
    950: '#401d08',
  },
  red: {
    50: '#fdf4f3',
    100: '#fbe7e5',
    200: '#f9d3cf',
    300: '#f4b4ad',
    400: '#ec887d',
    500: '#de594a',
    600: '#cc4536',
    700: '#ab372a',
    800: '#8e3126',
    900: '#762e26',
    950: '#40140f',
  },
  violet: {
    50: '#F6F4FD',
    100: '#EFECF9',
    200: '#E1DBF5',
    300: '#CABEEE',
    400: '#AF99E2',
    500: '#8A63D2',
    600: '#8453C7',
    700: '#7044A7',
    800: '#5E398C',
    900: '#4E3073',
    950: '#311E4D',
  },
  fuchsia: {
    50: '#FBF5FE',
    100: '#F8EBFC',
    200: '#F0D5F9',
    300: '#E6B4F3',
    400: '#D787EB',
    500: '#C662DE',
    600: '#A839C0',
    700: '#8D2C9F',
    800: '#752682',
    900: '#63246B',
    950: '#3F0C46',
  },
  teal: {
    50: '#F8FBFA',
    100: '#E5F4F0',
    200: '#CBE8E1',
    300: '#ACD7CF',
    400: '#8FC1B9',
    500: '#5BB3A6',
    600: '#429388',
    700: '#3A7670',
    800: '#33605B',
    900: '#30504D',
    950: '#162E2D',
  },
  simple: {
    white: '#ffffff',
    black: '#000000',
    transparent: 'transparent',
  },
};

export const defaultTheme = {
  colorToken: {
    border: primitiveColors.zinc[200],
    borderSuccess: primitiveColors.green[200],
    borderDanger: primitiveColors.red[200],
    borderInteractive: primitiveColors.brandBlue[200],
    borderWarning: primitiveColors.orange[200],
    borderNeutral: primitiveColors.zinc[200],
    borderBold: primitiveColors.zinc[400],
    base01: primitiveColors.simple.white,
    base02: primitiveColors.zinc[100],
    brandBase01: primitiveColors.brandBlue[900],
    brandBase02: primitiveColors.brandBlue[800],
    interactive: primitiveColors.brandBlue[600],
    interactiveLight: primitiveColors.brandBlue[100],
    warning: primitiveColors.orange[600],
    warningLight: primitiveColors.orange[100],
    success: primitiveColors.green[500],
    successLight: primitiveColors.green[100],
    neutral: primitiveColors.zinc[500],
    neutralLight: primitiveColors.zinc[100],
    danger: primitiveColors.red[500],
    dangerLight: primitiveColors.red[100],
    baseScore1: primitiveColors.red[600],
    baseScore2: primitiveColors.orange[600],
    baseScore3: primitiveColors.amber[600],
    baseScore4: primitiveColors.navy[600],
    baseScore5: primitiveColors.green[600],
    baseScoreLight1: primitiveColors.red[100],
    baseScoreLight2: primitiveColors.orange[100],
    baseScoreLight3: primitiveColors.amber[100],
    baseScoreLight4: primitiveColors.navy[100],
    baseScoreLight5: primitiveColors.green[100],
    borderBaseScore1: primitiveColors.red[200],
    borderBaseScore2: primitiveColors.orange[200],
    borderBaseScore3: primitiveColors.amber[200],
    borderBaseScore4: primitiveColors.navy[200],
    borderBaseScore5: primitiveColors.green[200],
    white: primitiveColors.simple.white,
    black: primitiveColors.simple.black,
    transparent: primitiveColors.simple.transparent,
  },
  textColor: {
    primary: primitiveColors.zinc[900],
    secondary: primitiveColors.zinc[500],
    tertiary: primitiveColors.zinc[300],
    textWhite: primitiveColors.simple.white,
    textBlack: primitiveColors.simple.black,
    link: primitiveColors.brandBlue[600],
    danger: primitiveColors.red[600],
    warning: primitiveColors.orange[600],
    success: primitiveColors.green[600],
    neutral: primitiveColors.zinc[600],
    score1: primitiveColors.red[700],
    score2: primitiveColors.orange[700],
    score3: primitiveColors.amber[700],
    score4: primitiveColors.navy[700],
    score5: primitiveColors.green[700],
  },
  chartColor: {
    chartBlue: primitiveColors.brandBlue['500'],
    chartAmber: primitiveColors.amber['500'],
    chartGreen: primitiveColors.green['500'],
    chartRed: primitiveColors.red['500'],
    chartViolet: primitiveColors.violet['500'],
    chartFuchsia: primitiveColors.fuchsia['500'],
    chartTeal: primitiveColors.teal['500'],
  },
  buttonColor: {
    primary: {
      info: {
        default: primitiveColors.brandBlue[600],
        hover: primitiveColors.brandBlue[500],
        press: primitiveColors.brandBlue[700],
      },
      danger: {
        default: primitiveColors.red[600],
        hover: primitiveColors.red[500],
        press: primitiveColors.red[700],
      },
      warning: {
        default: primitiveColors.orange[600],
        hover: primitiveColors.orange[500],
        press: primitiveColors.orange[700],
      },
      success: {
        default: primitiveColors.green[600],
        hover: primitiveColors.green[500],
        press: primitiveColors.green[700],
      },
      disabled: {
        default: primitiveColors.zinc[300],
      },
    },
    secondary: {
      default: {
        default: primitiveColors.simple.transparent,
        hover: primitiveColors.zinc[100],
        press: primitiveColors.zinc[400],
      },
      danger: {
        default: primitiveColors.simple.transparent,
        hover: primitiveColors.red[200],
        press: primitiveColors.red[400],
      },

      disabled: {
        default: primitiveColors.simple.transparent,
      },
    },
  },
  colors: {
    ...primitiveColors,
  },
  fontSize: {
    10: '10px',
    11: '11px',
    12: '12px',
    14: '14px',
    16: '16px',
    18: '18px',
    20: '20px',
    24: '24px',
    32: '32px',
    36: '36px',
    40: '40px',
  },
  fontWeight: {
    thin: 100,
    extraLight: 200,
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
  radius: {
    xxs: '2px',
    xs: '4px',
    s: '6px',
    m: '8px',
    xl: '12px',
    max: '999px',
  },
  spacing: {
    none: '0',
    2: '2px',
    4: '4px',
    6: '6px',
    8: '8px',
    10: '10px',
    12: '12px',
    14: '14px',
    16: '16px',
    20: '20px',
    24: '24px',
    28: '28px',
    32: '32px',
    36: '36px',
    40: '40px',
    44: '44px',
    48: '48px',
    56: '56px',
    64: '64px',
    80: '80px',
    96: '96px',
    112: '112px',
    128: '128px',
    144: '144px',
    160: '160px',
    176: '176px',
    192: '192px',
    208: '208px',
    320: '320px',
    360: '360px',
    480: '480px',
  },
  width: '100%',
  lineHeight: {
    lh1: '36px',
    lh2: '30px',
    lh3: '24px',
    lh4: '20px',
    lh5: '18px',
    lh6: '16px',
    lh7: '14px',
    lh8: '12px',
  },
  height: {
    h1: '0.25rem',
    h2: '0.5rem',
    h3: '0.75rem',
    h4: '1rem', // 16px
    h5: '2rem', // 32px
    h6: '3rem', // 48px
    h7: '4rem', // 64px
    footer: '83px',
    full: '100%',
  },
  deviceLayout: {
    desktop: {
      breakpoint: '1440px',
      width: '1344px',
      margin: '0 auto', // Centering the content, for this situation we can't use padding
      gap: '24px',
    },
    laptop: {
      breakpoint: '1024px',
      width: '100%',
      padding: '0 48px',
      gap: '24px',
    },
    tablet: {
      breakpoint: '768px',
      width: '100%',
      padding: '0 32px',
      gap: '12px',
    },
    mobile: {
      breakpoint: '767px',
      width: '100%',
      padding: '0 12px',
      gap: '12px',
    },
  },
};
